/**
 * HW Product Media Gallery Content - JS
 * Extracted from snippets/hw-product-media-gallery-content.liquid
 */

(function () {
  /** @type {WeakMap<Element, ReturnType<typeof setTimeout>>} */
  const hwWatchVideoResizeTimers = new WeakMap();

  function hwReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function hwVisibleSlides(slideshow) {
    return [...slideshow.querySelectorAll('slideshow-slide')].filter(
      (s) => !s.hasAttribute('hidden') || s.hasAttribute('reveal')
    );
  }

  function hwSlideHasVideo(slide) {
    if (!(slide instanceof HTMLElement)) return false;
    if (
      slide.classList.contains('product-media-container--video') ||
      slide.classList.contains('product-media-container--external_video')
    ) {
      return true;
    }
    return Boolean(
      slide.querySelector(
        '.product-media-container--video, .product-media-container--external_video'
      )
    );
  }

  function hwFirstVideoSlideIndex(slides) {
    return slides.findIndex((slide) => hwSlideHasVideo(slide));
  }

  /**
   * Slideshow select({ id }) needs slide-id. Base PDP markup often omits it; we
   * assign a stable id so hidden variant slides can still be revealed by
   * slideshow.js (see select() when slide has [hidden]).
   *
   * @param {HTMLElement} slide
   * @param {HTMLElement} slideshow
   * @returns {string}
   */
  function hwEnsureSlideId(slide, slideshow) {
    const existing = slide.getAttribute('slide-id');
    if (existing) return existing;

    const ordered = [...slideshow.querySelectorAll('slideshow-slide')];
    const idx = ordered.indexOf(slide);
    const id = `hw-slide-${idx >= 0 ? idx : 0}`;
    slide.setAttribute('slide-id', id);
    return id;
  }

  /**
   * First video slide in DOM order, including slides hidden for variant media.
   *
   * @param {HTMLElement} slideshow
   * @returns {HTMLElement | null}
   */
  function hwFirstVideoSlideAll(slideshow) {
    for (const slide of slideshow.querySelectorAll('slideshow-slide')) {
      if (hwSlideHasVideo(slide)) return slide;
    }
    return null;
  }

  function hwScrollVideoSlideIntoView(slideshow, slide) {
    if (!(slideshow instanceof HTMLElement) || !(slide instanceof HTMLElement)) {
      return;
    }

    slide.scrollIntoView({
      behavior: hwReducedMotion() ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  async function hwActivateGalleryVideo(wrapper) {
    const mediaGallery = wrapper.querySelector('media-gallery');
    if (!mediaGallery) return;

    const gridDesktop =
      mediaGallery.classList.contains('media-gallery--grid') &&
      window.matchMedia('(min-width: 750px)').matches;

    if (gridDesktop) {
      const container = mediaGallery.querySelector(
        '.media-gallery__grid .product-media-container--video, .media-gallery__grid .product-media-container--external_video'
      );
      if (!(container instanceof HTMLElement)) return;
      container.scrollIntoView({
        behavior: hwReducedMotion() ? 'auto' : 'smooth',
        block: 'center',
      });
      return;
    }

    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    const allSlides = [...slideshow.querySelectorAll('slideshow-slide')];
    const targetSlide = hwFirstVideoSlideAll(slideshow);
    if (!(targetSlide instanceof HTMLElement)) return;

    const slideId = hwEnsureSlideId(targetSlide, slideshow);

    hwScrollVideoSlideIntoView(slideshow, targetSlide);

    if (allSlides.length > 1 && typeof slideshow.select === 'function') {
      const selectOpts = { animate: !hwReducedMotion() };
      const runSelect = async () => {
        try {
          await slideshow.select({ id: slideId }, undefined, selectOpts);
        } catch {
          /* select() may throw if #scroll is not ready */
        }
      };

      await runSelect();
      /* Slideshow sometimes returns before #scroll is ready; retry briefly */
      if (typeof window.requestAnimationFrame === 'function') {
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
      }
      await runSelect();
      await new Promise((resolve) => setTimeout(resolve, 120));
      await runSelect();
    }
  }

  function hwSyncWatchVideoBadge(wrapper) {
    const badgeHost = wrapper.querySelector('[data-hw-watch-video-badge]');
    if (!badgeHost) return;

    const mediaGallery = wrapper.querySelector('media-gallery');
    if (!mediaGallery) {
      badgeHost.hidden = true;
      badgeHost.setAttribute('aria-hidden', 'true');
      return;
    }

    const gridDesktop =
      mediaGallery.classList.contains('media-gallery--grid') &&
      window.matchMedia('(min-width: 750px)').matches;

    let hasTarget = false;
    if (gridDesktop) {
      hasTarget = Boolean(
        mediaGallery.querySelector(
          '.media-gallery__grid .product-media-container--video, .media-gallery__grid .product-media-container--external_video'
        )
      );
    } else {
      const slideshow = mediaGallery.querySelector('slideshow-component');
      if (slideshow) {
        hasTarget = hwFirstVideoSlideIndex(hwVisibleSlides(slideshow)) >= 0;
      }
      if (!hasTarget) {
        const slidesAll = mediaGallery.querySelectorAll('slideshow-slide');
        hasTarget = [...slidesAll].some((s) => hwSlideHasVideo(s));
      }
    }

    badgeHost.hidden = !hasTarget;
    badgeHost.setAttribute('aria-hidden', hasTarget ? 'false' : 'true');
  }

  function hwScheduleWatchVideoSync(wrapper) {
    [0, 80, 250, 700].forEach((ms) => {
      setTimeout(() => hwSyncWatchVideoBadge(wrapper), ms);
    });
  }

  function wireWatchVideoBadge(wrapper) {
    if (wrapper.getAttribute('data-show-watch-video-badge') !== 'true') return;

    const badgeHost = wrapper.querySelector('[data-hw-watch-video-badge]');
    const trigger = wrapper.querySelector('[data-hw-watch-video-trigger]');
    if (!badgeHost || !trigger) return;

    if (wrapper.hasAttribute('data-hw-watch-video-wired')) return;
    wrapper.setAttribute('data-hw-watch-video-wired', 'true');

    const sync = () => hwSyncWatchVideoBadge(wrapper);
    hwScheduleWatchVideoSync(wrapper);

    trigger.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        void hwActivateGalleryVideo(wrapper);
      },
      true
    );

    const section = wrapper.closest('.shopify-section');
    if (section) {
      section.addEventListener('variant:update', () => {
        queueMicrotask(() => hwScheduleWatchVideoSync(wrapper));
      });
    }

    window.addEventListener('resize', () => {
      const prev = hwWatchVideoResizeTimers.get(wrapper);
      if (prev) clearTimeout(prev);
      hwWatchVideoResizeTimers.set(wrapper, setTimeout(sync, 150));
    });
  }

  function wireGalleryProgress(wrapper) {
    if (!wrapper || wrapper.hasAttribute('data-hw-gallery-progress-wired')) return true;

    const progressEl = wrapper.querySelector('[data-hw-gallery-progress]');
    const slideshow = wrapper.querySelector('slideshow-component');
    if (!progressEl || !slideshow) return false;

    const thumb = progressEl.querySelector('.hw-media-gallery-progress__thumb');
    const slideshowSlides = slideshow.querySelector('slideshow-slides');
    if (!thumb || !slideshowSlides) return false;

    const slideCount = slideshow.querySelectorAll('slideshow-slide').length;

    // Hide progress bar when only 1 slide exists
    if (slideCount <= 1) {
      progressEl.setAttribute('data-single-slide', 'true');
      wrapper.setAttribute('data-single-slide', 'true');
      thumb.style.width = '0%';
    } else {
      progressEl.removeAttribute('data-single-slide');
      wrapper.removeAttribute('data-single-slide');
    }

    const updateFromScroll = () => {
      if (slideCount <= 1) return;
      
      const scrollLeft = slideshowSlides.scrollLeft;
      const scrollWidth = slideshowSlides.scrollWidth;
      const clientWidth = slideshowSlides.clientWidth;
      const maxScroll = scrollWidth - clientWidth;

      if (maxScroll > 0) {
        const scrollProgress = Math.min(Math.max(scrollLeft / maxScroll, 0), 1);
        const fillWidth = 20 + scrollProgress * 80;
        thumb.style.width = `${fillWidth}%`;
      } else {
        thumb.style.width = '100%';
      }
    };

    const updateFromIndex = (index) => {
      if (slideCount <= 1) {
        thumb.style.width = '0%';
        return;
      }
      const progress = index / (slideCount - 1);
      const fillWidth = 20 + progress * 80;
      thumb.style.width = `${fillWidth}%`;
    };

    slideshowSlides.addEventListener('scroll', updateFromScroll);
    if (typeof slideshow.addEventListener === 'function') {
      slideshow.addEventListener('slideshow:select', (e) => {
        if (e.detail && typeof e.detail.index === 'number') {
          updateFromIndex(e.detail.index);
        } else {
          updateFromScroll();
        }
      });
    }
    window.addEventListener('resize', () => {
      setTimeout(updateFromScroll, 100);
    });
    updateFromScroll();
    wrapper.setAttribute('data-hw-gallery-progress-wired', 'true');
    return true;
  }

  function runWiring() {
    for (const wrapper of document.querySelectorAll('.hw-media-gallery-wrapper')) {
      wireWatchVideoBadge(wrapper);
      if (
        wrapper.querySelector('[data-hw-gallery-progress]') &&
        !wrapper.hasAttribute('data-hw-gallery-progress-wired')
      ) {
        wireGalleryProgress(wrapper);
      }
    }
  }

  runWiring();
  if (document.readyState !== 'complete') {
    window.addEventListener('load', runWiring);
  }

  document.addEventListener('shopify:section:load', runWiring);
})();
